<script setup lang="ts">
import { defineProps, defineEmits, ref, toRefs, watch, nextTick } from 'vue';
import { EMIT } from '@/enum';
import ProgressSpinner from 'primevue/progressspinner';

interface Props {
  src: string | null;
  isForPreview?: boolean;
  isLoading?: boolean;
  metaDuration?: number | null;
}

const props = withDefaults(defineProps<Props>(), {
  src: '',
  isForPreview: false,
  isLoading: false,
  metaDuration: null,
});

const { src, isForPreview, metaDuration } = toRefs(props);

const emit = defineEmits<{
  (e: EMIT.DISCARD): void;
  (e: EMIT.PLAY): void;
}>();

const audioRef = ref<HTMLAudioElement | null>(null);
const isPlaying = ref<boolean>(false);
const duration = ref<number>(metaDuration?.value || 0);
const currentTime = ref<number>(0);
const pendingPlay = ref<boolean>(false);

const shouldRecalcDuration = () => metaDuration.value == null || metaDuration.value === 0;

const formatTime = (sec: number) => {
  const s = Math.floor((sec || 0) % 60)
    .toString()
    .padStart(2, '0');
  const m = Math.floor((sec || 0) / 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
};

const onLoadedMetadata = async () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  await readDurationSafe();
  currentTime.value = el.currentTime || 0;
};

const onDurationChange = () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  const d = el.duration;
  if (Number.isFinite(d) && d > 0) duration.value = d;
};

const onTimeUpdate = () => {
  const el = audioRef.value;
  if (!el) return;
  currentTime.value = el.currentTime || 0;
  isPlaying.value = !el.paused;
};

const onEnded = () => {
  isPlaying.value = false;
  pendingPlay.value = false;
};

const onSeeked = () => {
  const el = audioRef.value;
  if (!el) return;
  currentTime.value = el.currentTime || 0;
};

const onSeek = (e: Event) => {
  const el = audioRef.value;
  if (!el) return;
  const target = e.target as HTMLInputElement;
  const val = Number(target.value);
  const t = isNaN(val) ? 0 : Math.min(Math.max(0, val), duration.value || 0);
  el.currentTime = t;
  currentTime.value = t;
};

const togglePlay = async () => {
  const el = audioRef.value;
  if (!el) return;

  if (isForPreview.value && (!src.value || src.value === '')) {
    pendingPlay.value = true;
    emit(EMIT.PLAY);
    return;
  }

  if (el.paused) {
    try {
      await el.play();
      isPlaying.value = true;
      if (shouldRecalcDuration() && !duration.value) {
        setTimeout(() => readDurationSafe(1000, 100), 120);
      }
    } catch {
      pendingPlay.value = true;
    }
  } else {
    el.pause();
    isPlaying.value = false;
  }
};

const onCanPlayThrough = async () => {
  const el = audioRef.value;
  if (!el) return;

  if (shouldRecalcDuration() && !duration.value) await onLoadedMetadata();

  if (pendingPlay.value) {
    pendingPlay.value = false;
    try {
      await el.play();
      isPlaying.value = true;
    } catch {
      isPlaying.value = false;
      pendingPlay.value = true;
      console.error('Error playing audio');
    }
  }
};

const onCanPlay = async () => {
  if (!shouldRecalcDuration()) return;
  const el = audioRef.value;
  if (!el) return;
  if (!Number.isFinite(duration.value) || duration.value === 0) {
    await readDurationSafe();
  }
};

const readDurationSafe = async (maxMs = 1500, interval = 100) => {
  const el = audioRef.value;
  if (!el) return;
  const start = performance.now();
  while (performance.now() - start < maxMs) {
    const d = el.duration;
    if (Number.isFinite(d) && d > 0) {
      duration.value = d;
      return;
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  duration.value = Number.isFinite(el.duration) ? el.duration : 0;
};

watch(
  () => src.value,
  async (val) => {
    await nextTick();

    const el = audioRef.value;
    if (!el) return;

    el.load();
    currentTime.value = 0;

    if (val && pendingPlay.value) {
      pendingPlay.value = false;
      try {
        await el.play();
        isPlaying.value = true;
      } catch (err) {
        console.error('Error playing audio after source change', err);
        isPlaying.value = false;
        pendingPlay.value = true;
      }
    } else if (shouldRecalcDuration()) {
      await onLoadedMetadata();
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <div class="audio-preview">
    <button v-if="!isForPreview" class="audio-control-icon control-icon" type="button" @click="emit('discard')">
      <span class="material-icons">delete</span>
    </button>

    <button class="icon-btn audio-control-icon" type="button" :title="isPlaying ? 'Pause' : 'Play'" @click="togglePlay">
      <ProgressSpinner v-if="isLoading" />
      <span v-else class="material-icons">{{ isPlaying ? 'pause' : 'play_arrow' }}</span>
    </button>
    <div class="audio-body">
      <input
        class="seek"
        type="range"
        min="0"
        :max="duration || 0"
        :value="currentTime"
        step="0.01"
        :disabled="!duration"
        @input="onSeek"
        @change="onSeek"
      />
      <div class="time-row">
        <span class="time current-time">{{ formatTime(currentTime) }}</span>
        <span class="time duration">{{ formatTime(duration) }}</span>
      </div>
    </div>
    <audio
      ref="audioRef"
      :src="src"
      preload="metadata"
      @loadedmetadata="onLoadedMetadata"
      @canplay="onCanPlay"
      @durationchange="onDurationChange"
      @timeupdate="onTimeUpdate"
      @seeked="onSeeked"
      @canplaythrough="onCanPlayThrough"
      @ended="onEnded"
    />
  </div>
</template>

<style scoped lang="scss">
.audio-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 95%;
  min-width: 220px;
  height: 3rem;
  margin-right: 1rem;
}

.control-icon {
  font-size: 1.25rem;
  cursor: pointer;
}

.audio-control-icon {
  font-size: 1rem;
  cursor: pointer;
  align-self: center;
  justify-self: center;
  border-radius: 50%;
  height: 2.25rem;
  width: 2.25rem;
  padding: auto;
  margin: 0.25rem;
  border: none;
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.audio-body {
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  flex: 1;
}

.seek {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.15);
  cursor: pointer;
}
.seek::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-color);
}
.seek::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--primary-color);
  border: none;
}

.time-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-top: 5px;

  .time {
    font-size: 12px;
  }
}

.icon-btn {
  background: none;
  border: none;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-btn .material-icons {
  font-size: 25px;
  line-height: 1;
}

.icon-btn:hover {
  color: var(--primary-color);
}
</style>
